import { useCallback, useEffect, useMemo, useState } from "react";
import _ from "lodash";

import { columnIsSortedByNumbers, sortListBy } from "utils/common";

import ListPagination from "components/ListPagination";
import ScrollableTable from "components/ScrollableTable";
import { useFilters } from "components/useFilters";
import { ORDER_BY_COLUMN_FILTER_KEY } from "components/useFilters/constants";

import { getBasedHeaders } from "entities/Reporting/components/ReportTable/utils";

interface IReportTable {
  rows: { [key: string]: string | null }[];
  columns: {
    children: {
      name: string;
      field_width: number;
      field: string;
    }[];
    name: string;
  }[];
  showLoading: boolean;
}

const LIMIT = 50;

const ReportTable = ({ rows, columns, showLoading }: IReportTable) => {
  const [page, setPage] = useState<number>(1);
  const { filters, updateFilter, deleteFilter } = useFilters();

  const handlePageChange = useCallback(
    (newPage: number) => {
      setPage(newPage);
      window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    },
    [setPage]
  );

  const selectedSortOption = useMemo(
    () => _.get(filters, ORDER_BY_COLUMN_FILTER_KEY),
    [filters]
  );

  const handleUpdateSortOption = useCallback(
    ({ key: option }: { key: string | undefined }) => {
      if (_.isUndefined(option)) {
        deleteFilter({ key: ORDER_BY_COLUMN_FILTER_KEY });
      } else {
        updateFilter({ key: ORDER_BY_COLUMN_FILTER_KEY, value: option });
      }
    },
    [deleteFilter, updateFilter]
  );

  const pagesCount = useMemo(() => Math.ceil(rows.length / LIMIT), [rows]);

  const limitedDeals = useMemo(() => {
    const isSortedByNumbers = columnIsSortedByNumbers({
      selectedSortOption,
      data: rows,
    });

    return sortListBy(rows, selectedSortOption, isSortedByNumbers).slice(
      (page - 1) * LIMIT,
      page * LIMIT
    );
  }, [rows, page, selectedSortOption]);

  useEffect(() => {
    // Reset the pagination each time the filters change
    if (!_.isEmpty(filters)) {
      setPage(1);
    }
  }, [filters]);

  const headers = useMemo(() => getBasedHeaders(columns), [columns]);

  return (
    <>
      <ScrollableTable
        rows={limitedDeals}
        headers={headers}
        sortBy={selectedSortOption}
        setSortBy={handleUpdateSortOption}
        showLoading={showLoading}
        tableDataTestid="deals-table-list"
        rowDataTestid="deal-data-row"
        rowCellDataTestid="deal-data-cell"
        sx={{
          maxHeight: "calc(100vh - 305px)",
        }}
      />
      <ListPagination
        currentPage={page}
        setPage={handlePageChange}
        pagesCount={pagesCount}
      />
    </>
  );
};

export default ReportTable;
